import { useCallback, useEffect, useState } from 'react';
import { Flex } from 'antd';

import { ItRequisitionStatusCodes } from '@Enum/api/ItRequisitionStatusCodes';
import {
  useAppSelector,
  useRequestTypeFinderById,
  useStatusFinderByNum,
  useUserPermissions,
} from '@Hooks/index';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import {
  useGetExistingPackageMutation,
  useGetITRequestFormQuery,
} from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import { Status } from '@Components/Base';
import {
  getITAccessories,
  getOrganizationOptions,
} from '@Store/selectors/dictionarySelector';
import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import { Option } from '@Components/types/options';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import {
  useDownloadDocumentQuery,
  useFormListMutation,
} from '@Store/api/dmsApiSlice';
import { getDocument } from '@Features/utils/document';

import {
  transformDictToOptions,
  transformProjectsToOptions,
} from '@Utils/transformToOptions';

import { useGetProjectsForOrgMutation } from '@Store/api/dictionaryApiSlice';
import fieldSource from '../FieldSource';
import { EditAction, FooterActions } from './components';
import styles from './view-mobile-form.module.css';
import { getDocumentFolder } from '../../utils/document';

interface RequestViewMobileFormProps {
  onEdit: (recordKey: string) => void;
  onDeleteRequest: (requestNo: string) => void;
  onSubmitRequest: (requestNo: string) => void;
  onCancelRequest: (requestNo: string) => void;
}

const RequestViewMobileForm: React.FC<RequestViewMobileFormProps> = ({
  onEdit,
  onDeleteRequest,
  onSubmitRequest,
  onCancelRequest,
}) => {
  const { findStatus } = useStatusFinderByNum('ITRequisition');
  const { findRequestType } = useRequestTypeFinderById('ITRequisition');
  const [getExistingPackage] = useGetExistingPackageMutation();
  const [getProjects] = useGetProjectsForOrgMutation();
  const [projects, setProjects] = useState<Option[]>([]);
  const permissions = useUserPermissions();

  const [documentId, setDocumentId] = useState('');

  const { data: imageURL } = useDownloadDocumentQuery(documentId || '', {
    skip: !documentId,
  });

  const [existingValue, setExistingValue] = useState('');

  const currentUser = useAppSelector(currentUserSelector);
  const requestId = useAppSelector(getModalRecordKey);
  const organizations = useAppSelector(getOrganizationOptions);
  const itAccessories = useAppSelector(getITAccessories);

  const { data } = useGetITRequestFormQuery(requestId!, {
    skip: !requestId,
    refetchOnMountOrArgChange: true,
  });
  const { data: users = [] } = useGetUsersListByNameQuery(null);
  const [getFormList] = useFormListMutation();

  useEffect(() => {
    if (data && data.organizations) {
      getProjects(data.organizations.join(''))
        .unwrap()
        .then((projectsResp) => {
          const transformedProjects = transformProjectsToOptions(projectsResp);
          setProjects(transformedProjects);
        })
        .catch(() => {
          setProjects([]);
        });
    }
  }, [data]);

  useEffect(() => {
    if (!data?.fileName) {
      return;
    }

    const documentFolderPath = getDocumentFolder(data.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, data.fileName);

        if (matchedDocument) {
          setDocumentId(matchedDocument.documentId);
        }
      });
  }, [data]);

  const handleDownloadClick = useCallback((): void => {
    if (imageURL && data?.fileName) {
      const a = document.createElement('a');
      a.href = imageURL;
      a.download = data?.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }, [imageURL, data?.fileName]);

  useEffect(() => {
    if (data) {
      if (data.statusId === ItRequisitionStatusCodes.AWAITING_APPROVAL) {
        getExistingPackage(data.reqItemId)
          .unwrap()
          .then((response) => setExistingValue(response?.packages || ''));
      }
    }
  }, [data]);

  if (!data) return null;

  const statusRequest = findStatus(data?.statusId)?.codeDesc || '';

  return (
    <Flex vertical>
      <div className={styles.viewMobileForm}>
        {fieldSource(
          data,
          findRequestType,
          organizations,
          projects,
          transformDictToOptions(itAccessories),
          users,
          currentUser,
          documentId,
          existingValue,
          handleDownloadClick
        ).map((item) => (
          <div className={styles.viewMobileItem} key={item.label}>
            <span>{item.label}</span>
            {item.label === 'Status' ? (
              <Status
                className={styles.viewMobileItemValue}
                text={findStatus(Number(item.value))?.codeDesc || ''}
              />
            ) : (
              <span className={styles.viewMobileItemValue}>{item.value}</span>
            )}
          </div>
        ))}
      </div>
      <EditAction handleEdit={() => onEdit(requestId)} status={statusRequest} />
      <FooterActions
        handleDeleteRequest={() => onDeleteRequest(requestId)}
        handleSubmitRequest={() => onSubmitRequest(requestId)}
        handleCancelRequest={() => onCancelRequest(requestId)}
        disabledCancelForm={!permissions.can('Cancel', 'ITRequisition')}
        disabledDeleteForm={!permissions.can('Delete', 'ITRequisition')}
        disabledSubmitForm={!permissions.can('Submit', 'ITRequisition')}
        status={statusRequest}
      />
    </Flex>
  );
};

export default RequestViewMobileForm;
